// filepath: src/pages/attendance/DownloadAttendancePage.jsx
import React, { useState, useEffect, useMemo, Fragment } from 'react';
import { toast } from 'react-toastify';
import API from '../../services/API';

const DownloadAttendancePage = () => {
  const [dateStr, setDateStr] = useState('');
  const [monthStr, setMonthStr] = useState(''); // YYYY-MM
  const [filterType, setFilterType] = useState('day'); // day | month
  const [isPortal] = useState(true); // portal context on this page
  const [isDownloading, setIsDownloading] = useState(false);
  const [lastFilename, setLastFilename] = useState('');

  // Classes / filters
  const [classes, setClasses] = useState([]);
  const [uniqueForms, setUniqueForms] = useState([]);
  const [incompleteClasses, setIncompleteClasses] = useState([]); // for diagnostics
  const [selectedForm, setSelectedForm] = useState(''); // '' = All
  const [selectedClassId, setSelectedClassId] = useState(''); // optional
  const [startTime, setStartTime] = useState('');
  const [amalanScope, setAmalanScope] = useState('all');
  const [amalanMonth, setAmalanMonth] = useState('');
  const [amalanLimit, setAmalanLimit] = useState(50);
  const [rankingYear, setRankingYear] = useState(new Date().getFullYear().toString());

  const [customTypeId, setCustomTypeId] = useState('');
  const [customTypes, setCustomTypes] = useState([]);
  const [isCustomDownloading, setIsCustomDownloading] = useState(false);

  const buildHeaders = () => ({
    'Content-Type': 'application/json',
    'Accept': 'text/csv,application/json',
    'token': localStorage.getItem('token') || '',
  });

  // Extract filename from Content-Disposition
  const getFilenameFromDisposition = (disposition, fallback) => {
    if (!disposition) return fallback;
    const starMatch = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
    if (starMatch?.[1]) {
      try { return decodeURIComponent(starMatch[1]); } catch { /* ignore */ }
    }
    const plainMatch = /filename\s*=\s*"([^"]+)"/i.exec(disposition) || /filename\s*=\s*([^;]+)/i.exec(disposition);
    return (plainMatch?.[1]?.trim()) || fallback;
  };

  // Fetch classes from the API (portal)
  const fetchClasses = () => {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'token': localStorage.getItem('token'),
    };

    API.get('/portal/getClasses', { headers })
      .then((response) => {
        if (response.data.response_code === 1000) {
          const fetched = response.data.data || [];
          setClasses(fetched);

          const formsFromAPI = Array.from(
            new Set(
              fetched
                .map(c => c.tingkatan_moeis?.toString().toUpperCase().trim())
                .filter(Boolean)
            )
          );
          setUniqueForms(formsFromAPI);

          const missing = fetched.filter(c => !c.tingkatan_moeis || !c.class_moeis_format);
          setIncompleteClasses(missing);
        } else {
          toast.error(`${response.data.message}`, { autoClose: 5000 });
        }
      })
      .catch(() => {
        toast.error("Error fetching classes", { autoClose: 5000 });
      });
  };
  const fetchCustomAttendanceTypes = () => {
    const headers = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      token: localStorage.getItem('token'),
    };

    API.get('/customAttendance/type?is_portal=true', { headers })
      .then((response) => {
        if (response.data.response_code === 1000) {
          setCustomTypes(response.data.data || []);
        } else {
          toast.error(response.data.message || 'Error fetching custom attendance types');
        }
      })
      .catch(() => {
        toast.error('Error fetching custom attendance types');
      });
  };
  useEffect(() => {
    fetchClasses();
    fetchCustomAttendanceTypes();
  }, []);

  // Filter classes by selected form
  const filteredClasses = useMemo(() => {
    if (!selectedForm) return classes;
    return classes.filter(
      c => (c.tingkatan_moeis?.toString().toUpperCase().trim() || '') === selectedForm
    );
  }, [classes, selectedForm]);

  // Reset class when form changes
  useEffect(() => {
    setSelectedClassId('');
  }, [selectedForm]);

  // Clear irrelevant date field when switching type
  useEffect(() => {
    if (filterType === 'day') setMonthStr('');
    if (filterType === 'month') setDateStr('');
  }, [filterType]);

  const displayClassLabel = (c) => {
    // Why: show the clearest class identifier available to users
    return c.class_moeis_format?.trim()
      || c.class_name?.trim()
      || c.class_code?.trim()
      || `Class ${c.class_id}`;
  };

  const handleDownload = async () => {
    // Validate required input based on filterType
    if (filterType === 'day' && !dateStr) {
      toast.error('Please select a date (YYYY-MM-DD).');
      return;
    }
    if (filterType === 'month' && !monthStr) {
      toast.error('Please select a month (YYYY-MM).');
      return;
    }

    const baseDate = filterType === 'day' ? dateStr : `${monthStr}-01`;

    setIsDownloading(true);
    const url = `/attendance/downloadAttendance${isPortal ? '?is_portal=true' : ''}`;
    const payload = {
      filter_type: filterType,
      date: baseDate,
      ...(selectedClassId ? { class_id: selectedClassId } : {}),
      ...(selectedForm ? { form: selectedForm.toUpperCase().trim() } : {}),
      ...(startTime ? { start_time: startTime } : {}),
      // If you also send form, ensure mutual exclusivity on backend.
    };

    try {
      const res = await API.post(url, payload, {
        headers: buildHeaders(),
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();
      const isJson = contentType.includes('application/json');
      if (isJson) {
        const text = await res.data.text();
        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Download failed.');
        } catch { toast.error('Download failed.'); }
        return;
      }

      const suffix = filterType === 'day' ? dateStr : monthStr;
      let fallbackName = `attendance_${filterType}_${suffix}`;
      if (selectedClassId) fallbackName += `_class_${selectedClassId}`;
      if (startTime) fallbackName += `_from_${startTime.replace(':', '')}`;
      fallbackName += `.csv`;
      const cd = res.headers?.['content-disposition'];
      const filename = getFilenameFromDisposition(cd, fallbackName);

      const blob = new Blob([res.data], { type: contentType || 'text/csv;charset=utf-8' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);

      setLastFilename(filename);
      toast.success('CSV downloaded.');
    } catch (err) {
      const blob = err?.response?.data;
      const contentType = (err?.response?.headers?.['content-type'] || '').toLowerCase();
      if (blob && contentType.includes('application/json') && blob.text) {
        const text = await blob.text();
        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Download failed.');
        } catch { toast.error('Download failed.'); }
      } else {
        toast.error('Download failed.');
      }
    } finally {
      setIsDownloading(false);
    }
  };
  const handleSummaryDownload = async () => {
    if (!monthStr) {
      toast.error('Please select a month (YYYY-MM).');
      return;
    }

    const baseDate = `${monthStr}-01`;
    setIsDownloading(true);

    const url = `/attendance/attendanceSummaryView${isPortal ? '?is_portal=true' : ''}`;
    const payload = {
      filter_type: 'month',
      date: baseDate,
      ...(selectedClassId ? { class_id: selectedClassId } : {}),
      ...(selectedForm ? { form: selectedForm.toUpperCase().trim() } : {}),
    };

    try {
      const res = await API.post(url, payload, {
        headers: buildHeaders(),
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const obj = JSON.parse(text);
        toast.error(obj?.message || 'Download failed.');
        return;
      }

      const cd = res.headers?.['content-disposition'];
      const fallbackName = `attendance_summary_${monthStr}.csv`;
      const filename = getFilenameFromDisposition(cd, fallbackName);

      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);

      setLastFilename(filename);
      toast.success('Attendance summary downloaded.');
    } catch (err) {
      console.error(err);
      toast.error('Download failed.');
    } finally {
      setIsDownloading(false);
    }
  };
  const handleCustomAttendanceSummaryDownload = async () => {
    if (!monthStr) {
      toast.error('Please select a month (YYYY-MM).');
      return;
    }

    const baseDate = `${monthStr}-01`;
    setIsCustomDownloading(true);

    const url = `/customAttendance/attendanceSummaryView${isPortal ? '?is_portal=true' : ''}`;

    const payload = {
      filter_type: 'month',
      date: baseDate,
      include_school_days_only: false,
      ...(selectedClassId ? { class_id: selectedClassId } : {}),
      ...(selectedForm ? { form: selectedForm.toUpperCase().trim() } : {}),
      ...(customTypeId ? { type_id: customTypeId } : {}),
    };

    try {
      const res = await API.post(url, payload, {
        headers: buildHeaders(),
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();

      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Download failed.');
        } catch {
          toast.error('Download failed.');
        }
        return;
      }

      const cd = res.headers?.['content-disposition'];
      const fallbackName = customTypeId
        ? `custom_attendance_summary_${monthStr}_type_${customTypeId}.csv`
        : `custom_attendance_summary_${monthStr}.csv`;

      const filename = getFilenameFromDisposition(cd, fallbackName);

      const blob = new Blob([res.data], { type: contentType || 'text/csv;charset=utf-8' });
      const blobUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(blobUrl);

      setLastFilename(filename);
      toast.success('Custom attendance summary downloaded.');
    } catch (err) {
      console.error(err);

      const blob = err?.response?.data;
      const contentType = (err?.response?.headers?.['content-type'] || '').toLowerCase();

      if (blob && contentType.includes('application/json') && blob.text) {
        const text = await blob.text();
        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Download failed.');
        } catch {
          toast.error('Download failed.');
        }
      } else {
        toast.error('Download failed.');
      }
    } finally {
      setIsCustomDownloading(false);
    }
  };
  const handleAmalanBaikDownload = async () => {
    const scope = amalanScope; // "month" | "year" | "all"
    const month = amalanMonth;
    const limit = amalanLimit || 50;

    if (scope === 'month' && !month) {
      toast.error('Please select a month (YYYY-MM).');
      return;
    }

    const params = new URLSearchParams({ scope, limit_top: limit, is_portal: true });
    if (scope === 'month') params.append('month', month);

    const url = `/goodDeeds/downloadTopStudents?${params.toString()}`;
    const headers = {
      token: localStorage.getItem('token'),
      Accept: 'text/csv,application/json',
    };

    try {
      const res = await API.get(url, {
        headers,
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const obj = JSON.parse(text);
        toast.error(obj?.message || 'Download failed.');
        return;
      }

      const cd = res.headers?.['content-disposition'];
      const fallbackName = `amalan_baik_${scope}_${month || 'all'}.csv`;
      const filename =
        (cd && /filename="?([^"]+)"?/.exec(cd)?.[1]) || fallbackName;

      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Amalan Baik CSV downloaded.');
    } catch (err) {
      console.error(err);
      const blob = err?.response?.data;
      if (blob?.text) {
        const text = await blob.text();
        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Download failed.');
        } catch {
          toast.error('Download failed.');
        }
      } else {
        toast.error('Download failed.');
      }
    }
  };
  const handleYearlyRankingDownload = async () => {
    if (!rankingYear) {
      toast.error('Please select a year (YYYY).');
      return;
    }

    setIsDownloading(true);
    const url = `/attendance/attendanceDownloadBySchoolView${isPortal ? '?is_portal=true' : ''}`;
    const payload = {
      filter_type: 'year', // backend will always generate full-year data
      date: `${rankingYear}-01-01`,
      ranking: true,
    };

    try {
      const res = await API.post(url, payload, {
        headers: buildHeaders(),
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const obj = JSON.parse(text);
        toast.error(obj?.message || 'Download failed.');
        return;
      }

      const cd = res.headers?.['content-disposition'];
      const fallbackName = `attendance_ranking_${rankingYear}.csv`;
      const filename = getFilenameFromDisposition(cd, fallbackName);

      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);

      setLastFilename(filename);
      toast.success('✅ Yearly Attendance Ranking CSV downloaded.');
    } catch (err) {
      console.error(err);
      toast.error('Download failed.');
    } finally {
      setIsDownloading(false);
    }
  };
  const handleClassTimesheetDownload = async () => {
    if (!selectedClassId) {
      toast.error('Please select a class.');
      return;
    }
    if (!monthStr) {
      toast.error('Please select a month (YYYY-MM).');
      return;
    }

    setIsDownloading(true);

    const params = new URLSearchParams({
      class_id: selectedClassId,
      month: monthStr,
    });

    const url = `/attendance/timesheet/download/class?${params.toString()}`;

    try {
      const res = await API.get(url, {
        headers: {
          token: localStorage.getItem('token'),
          Accept: 'text/csv,application/json',
        },
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();
      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        const obj = JSON.parse(text);
        toast.error(obj?.message || 'Download failed.');
        return;
      }

      const cd = res.headers?.['content-disposition'];
      const fallbackName = `timesheet_class_${selectedClassId}_${monthStr}.csv`;
      const filename = getFilenameFromDisposition(cd, fallbackName);

      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);

      setLastFilename(filename);
      toast.success('Class timesheet downloaded.');
    } catch (err) {
      console.error(err);
      toast.error('Download failed.');
    } finally {
      setIsDownloading(false);
    }
  };
  const handleAmalanBurukDownload = async () => {
    const scope = amalanScope; // "month" | "all"
    const month = amalanMonth;
    const limit = amalanLimit || 50;

    if (scope === 'month' && !month) {
      toast.error('Please select a month (YYYY-MM).');
      return;
    }

    const params = new URLSearchParams({
      scope,
      limit_top: limit,
      is_portal: true,
    });

    if (scope === 'month') {
      params.append('month', month);
    }

    const url = `/badDeeds/downloadBadDeedsByMonth?${params.toString()}`;

    const headers = {
      token: localStorage.getItem('token'),
      Accept: 'text/csv,application/json',
    };

    try {
      const res = await API.get(url, {
        headers,
        responseType: 'blob',
        transformResponse: [(data) => data],
      });

      const contentType = (res.headers?.['content-type'] || '').toLowerCase();

      if (contentType.includes('application/json')) {
        const text = await res.data.text();
        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Download failed.');
        } catch {
          toast.error('Download failed.');
        }
        return;
      }

      const cd = res.headers?.['content-disposition'];
      const fallbackName = `amalan_buruk_${scope}_${month || 'all'}.csv`;
      const filename =
        (cd && /filename="?([^"]+)"?/.exec(cd)?.[1]) || fallbackName;

      const blob = new Blob([res.data], {
        type: contentType || 'text/csv;charset=utf-8',
      });

      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);

      toast.success('Amalan Buruk CSV downloaded.');
    } catch (err) {
      console.error(err);

      const blob = err?.response?.data;
      if (blob?.text) {
        const text = await blob.text();
        try {
          const obj = JSON.parse(text);
          toast.error(obj?.message || 'Download failed.');
        } catch {
          toast.error('Download failed.');
        }
      } else {
        toast.error('Download failed.');
      }
    }
  };
  const isDownloadDisabled =
    isDownloading ||
    (filterType === 'day' ? !dateStr : !monthStr);

  return (
    <Fragment>
      <div className="container-fluid">
        <div className="card rounded-25">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Download Attendance File</h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {/* Filter Type first */}
              <div className="col-md-3">
                <label className="form-label">Filter Type</label>
                <select
                  className="form-select"
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="day">Day</option>
                  <option value="month">Month</option>
                </select>
              </div>

              {/* Conditional date/month picker */}
              {filterType === 'day' ? (
                <div className="col-md-3">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dateStr}
                    onChange={(e) => setDateStr(e.target.value)}
                  />
                  <small className="text-muted">Exact date.</small>
                </div>
              ) : (
                <div className="col-md-3">
                  <label className="form-label">Month</label>
                  <input
                    type="month"
                    className="form-control"
                    value={monthStr}
                    onChange={(e) => setMonthStr(e.target.value)}
                  />
                  <small className="text-muted">Whole month.</small>
                </div>
              )}

              {/* Form (optional) */}
              <div className="col-md-3">
                <label className="form-label">Form (optional)</label>
                <select
                  className="form-select"
                  value={selectedForm}
                  onChange={(e) => setSelectedForm(e.target.value)}
                >
                  <option value="">All Forms</option>
                  {uniqueForms.map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Start Time (optional)</label>
                <input
                  type="time"
                  className="form-control"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                />
                <small className="text-muted">Filter records after this time.</small>
              </div>

              {/* Class (optional) */}
              <div className="col-md-3">
                <label className="form-label">Class (optional)</label>
                <select
                  className="form-select"
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                >
                  <option value="">All Classes</option>
                  {filteredClasses.map(c => (
                    <option key={c.class_id} value={c.class_id}>
                      {displayClassLabel(c)}
                    </option>
                  ))}
                </select>
                {incompleteClasses.length > 0 && (
                  <small className="text-warning d-block mt-1">
                    Note: {incompleteClasses.length} class(es) missing MOEIS fields.
                  </small>
                )}
              </div>
            </div>

            <div className="mt-4 d-flex align-items-center gap-2">
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={isDownloadDisabled}
              >

                {isDownloading ? 'Downloading…' : 'Download Detailed CSV'}
              </button>

              <button
                className="btn btn-outline-success"
                onClick={handleSummaryDownload}
                disabled={isDownloading || !monthStr}
              >
                {isDownloading ? <span
                  className="spinner-border spinner-border-sm text-success"
                  role="status"
                  aria-hidden="true"
                ></span> : null}
                {isDownloading ? 'Downloading…' : 'Download Summary CSV'}
              </button>

              <button
                className="btn btn-outline-primary"
                onClick={handleClassTimesheetDownload}
                disabled={isDownloading || !selectedClassId || !monthStr}
              >
                {isDownloading ? 'Downloading…' : 'Download Class Timesheet'}
              </button>

              {lastFilename && (
                <span className="text-muted">Last downloaded: <strong>{lastFilename}</strong></span>
              )}
            </div>


            <hr className="my-4" />
            <ul className="small text-muted mb-0">
              <li>Select <strong>Day</strong> or <strong>Month</strong> first; the correct picker will appear.</li>
              <li>Optional class filter: when selected, server receives <code>class_id</code>.</li>
              <li>Status values: Absent(0), Check-In(1), Check-Out(2), No Record(3).</li>
              <li>“Absent Reason” is populated only for Absent(0).</li>
            </ul>
          </div>
        </div>

        {/* ---- NEW: Download Custom Attendance Summary ---- */}
        <div className="card rounded-25 mt-5">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Download Custom Attendance Summary</h5>
          </div>

          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Month</label>
                <input
                  type="month"
                  className="form-control"
                  value={monthStr}
                  onChange={(e) => setMonthStr(e.target.value)}
                />
                <small className="text-muted">Whole month.</small>
              </div>

              <div className="col-md-3">
                <label className="form-label">Custom Attendance Type</label>
                <select
                  className="form-select"
                  value={customTypeId}
                  onChange={(e) => setCustomTypeId(e.target.value)}
                >
                  <option value="">All Custom Attendance Types</option>
                  {customTypes.map((type) => (
                    <option key={type.type_id} value={type.type_id}>
                      {type.name || `Type ${type.type_id}`}
                    </option>
                  ))}
                </select>
                <small className="text-muted">Optional event/type filter.</small>
              </div>

              <div className="col-md-3">
                <label className="form-label">Form (optional)</label>
                <select
                  className="form-select"
                  value={selectedForm}
                  onChange={(e) => setSelectedForm(e.target.value)}
                >
                  <option value="">All Forms</option>
                  {uniqueForms.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label">Class (optional)</label>
                <select
                  className="form-select"
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                >
                  <option value="">All Classes</option>
                  {filteredClasses.map((c) => (
                    <option key={c.class_id} value={c.class_id}>
                      {displayClassLabel(c)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 d-flex align-items-center gap-2">
              <button
                className="btn btn-outline-dark"
                onClick={handleCustomAttendanceSummaryDownload}
                disabled={isCustomDownloading || !monthStr}
              >
                {isCustomDownloading ? 'Downloading…' : 'Download Custom Attendance Summary CSV'}
              </button>
            </div>

            <hr className="my-4" />

            <ul className="small text-muted mb-0">
              <li>Downloads custom attendance by month, similar to the normal attendance summary.</li>
              <li>Use Custom Attendance Type to download one event only, or leave it as All.</li>
              <li>Output marks each date with <code>X</code> when a student has a custom attendance record.</li>
            </ul>
          </div>
        </div>
                {/* ---- NEW: Download Amalan Buruk Section ---- */}
        <div className="card rounded-25 mt-5">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Download Amalan Buruk File</h5>
          </div>

          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Scope</label>
                <select
                  className="form-select"
                  value={amalanScope}
                  onChange={(e) => setAmalanScope(e.target.value)}
                >
                  <option value="month">Month</option>
                  <option value="all">All Time</option>
                </select>
              </div>

              {amalanScope === 'month' && (
                <div className="col-md-3">
                  <label className="form-label">Month</label>
                  <input
                    type="month"
                    className="form-control"
                    value={amalanMonth}
                    onChange={(e) => setAmalanMonth(e.target.value)}
                  />
                </div>
              )}

              <div className="col-md-3">
                <label className="form-label">Student Limit</label>
                <select
                  className="form-select"
                  value={amalanLimit}
                  onChange={(e) => setAmalanLimit(e.target.value)}
                >
                  <option value="50">Top 50 students</option>
                  <option value="100">Top 100 students</option>
                  <option value="200">Top 200 students</option>
                  <option value="all">All students</option>
                </select>
                <small className="text-muted">Select “All students” to include everyone.</small>
              </div>
            </div>

            <div className="mt-4 d-flex align-items-center gap-2">
              <button
                className="btn btn-danger"
                onClick={handleAmalanBurukDownload}
              >
                Download CSV
              </button>
            </div>

            <hr className="my-4" />

            <ul className="small text-muted mb-0">
              <li>
                Download the <strong>Amalan Buruk Students</strong> by month or all-time.
              </li>
              <li>
                The file includes separate sections for Morning and Afternoon sessions.
              </li>
              <li>
                Use <code>limit_top</code> to set how many students to include.
              </li>
            </ul>
          </div>
        </div>
        {/* ---- NEW: Download Amalan Baik Section ---- */}
        <div className="card rounded-25 mt-5">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Download Amalan Baik File</h5>
          </div>
          <div className="card-body">
            <div className="row g-3">
              {/* Scope */}
              <div className="col-md-3">
                <label className="form-label">Scope</label>
                <select
                  className="form-select"
                  value={amalanScope}
                  onChange={(e) => setAmalanScope(e.target.value)}
                >
                  <option value="month">Month</option>
                  {/* <option value="year">Year</option>  */}
                  <option value="all">All Time</option>
                </select>
              </div>

              {/* Month selector (only if scope=month) */}
              {amalanScope === 'month' && (
                <div className="col-md-3">
                  <label className="form-label">Month</label>
                  <input
                    type="month"
                    className="form-control"
                    value={amalanMonth}
                    onChange={(e) => setAmalanMonth(e.target.value)}
                  />
                </div>
              )}

              {/* Limit Top */}
              <div className="col-md-3">
                <label className="form-label">Student Limit</label>
                <select
                  className="form-select"
                  value={amalanLimit}
                  onChange={(e) => setAmalanLimit(e.target.value)}
                >
                  <option value="50">Top 50 students</option>
                  <option value="100">Top 100 students</option>
                  <option value="200">Top 200 students</option>
                  <option value="all">All students</option>
                </select>
                <small className="text-muted">Select “All students” to include everyone.</small>
              </div>

            </div>

            <div className="mt-4 d-flex align-items-center gap-2">
              <button
                className="btn btn-success"
                onClick={handleAmalanBaikDownload}
              >
                Download CSV
              </button>
            </div>

            <hr className="my-4" />
            <ul className="small text-muted mb-0">
              <li>
                Download the <strong>Top Amalan Baik Students</strong> by month, year,
                or all-time.
              </li>
              <li>
                The file includes separate sections for Morning and Afternoon sessions.
              </li>
              <li>
                Use <code>limit_top</code> to set how many students to include (default 50).
              </li>
            </ul>
          </div>
        </div>
        {/* ---- NEW: Download Yearly Attendance Ranking ---- */}
        <div className="card rounded-25 mt-5">
          <div className="card-header d-flex align-items-center justify-content-between">
            <h5 className="mb-0">Download Yearly Attendance Ranking</h5>
          </div>
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <small className="text-muted">Full-year attendance ranking (Jan–Dec).</small>
                <div className="">
                  <button
                    className="btn btn-outline-primary"
                    onClick={handleYearlyRankingDownload}
                    disabled={isDownloading || !rankingYear}
                  >
                    {isDownloading ? 'Downloading…' : 'Download Yearly Ranking CSV'}
                  </button>
                </div>
              </div>
            </div>
            <hr className="my-4" />
            <ul className="small text-muted mb-0">
              <li>Generates a <strong>full-year</strong> attendance leaderboard.</li>
              <li>Students are ranked by highest attendance percentage.</li>
              <li>The output CSV includes: Rank, Student Name, Class, Attended Days, Total Days, Attendance %.</li>
              <li>File name format: <code>attendance_ranking_YYYY.csv</code>.</li>
            </ul>
          </div>
        </div>

      </div>
    </Fragment>
  );
};

export default DownloadAttendancePage;
